const veSearchRanking = (() => {
  const cache = new Map();
  const normalize = value => String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  function score(title, query, materials = '') {
    const name = normalize(title);
    const term = normalize(query);
    if (!term) return [0, 0, 0];
    const materialName = normalize(Array.isArray(materials) ? materials.join(' ') : materials || '');
    const materialWords = materialName.split(' ');
    const words = [...name.split(' '), ...materialWords];
    const tokens = [...new Set(term.split(' '))];
    const exact = tokens.filter(token => words.includes(token)).length;
    const prefix = tokens.filter(token => words.some(word => word.startsWith(token))).length;
    let tier = 0;
    if (name === term || name.replace(/^the /, '') === term) tier = 4;
    else if ((' ' + name + ' ').includes(' ' + term + ' ')) tier = 3;
    else if (exact === tokens.length && tokens.some(token => materialWords.includes(token))) {
      const titleTerms = tokens.filter(token => !materialWords.includes(token)).join(' ');
      tier = !titleTerms || (' ' + name + ' ').includes(' ' + titleTerms + ' ') ? 3 : 2;
    }
    else if (exact === tokens.length) tier = 2;
    else if (prefix === tokens.length) tier = 1;
    return [tier, exact, prefix];
  }
  function rank(products, query) {
    return products.map(product => ({product, score:score(product.title, query, product.materials)})).sort((a, b) => {
      for (let index = 0; index < a.score.length; index++) {
        if (a.score[index] !== b.score[index]) return b.score[index] - a.score[index];
      }
      return Number(b.product.created) - Number(a.product.created) || String(a.product.id).localeCompare(String(b.product.id));
    }).map(entry => entry.product);
  }
  async function mapPages(pages, task) {
    const output = new Array(pages.length);
    let cursor = 0;
    await Promise.all(Array.from({length:Math.min(3, pages.length)}, async () => {
      while (cursor < pages.length) {
        const index = cursor++;
        output[index] = await task(pages[index]);
      }
    }));
    return output;
  }
  function searchURL(endpoint, parameters) {
    const url = new URL(endpoint, location.origin);
    url.search = new URLSearchParams(parameters);
    url.searchParams.set('type', 'product');
    url.searchParams.set('sort_by', 'relevance');
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.set('view', 've-ranked');
    return url;
  }
  async function load(endpoint, parameters, signal) {
    const url = searchURL(endpoint, parameters);
    const key = url.href;
    const stored = cache.get(key);
    if (stored && Date.now() - stored.time < 60000) return stored.products;
    async function page(number) {
      const request = new URL(url);
      request.searchParams.set('page', number);
      const response = await fetch(request, {signal});
      if (!response.ok) throw new Error('Search unavailable');
      const data = await response.json();
      if (!Array.isArray(data.products) || data.page !== number || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100) throw new Error('Incomplete search data');
      return data;
    }
    const first = await page(1);
    const rest = await mapPages(Array.from({length:first.pages - 1}, (_, index) => index + 2), page);
    const products = [first, ...rest].flatMap(data => data.products);
    if (new Set(products.map(product => String(product.id))).size !== products.length || products.length !== first.total) throw new Error('Search changed while loading');
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (cache.size >= 4) cache.delete(cache.keys().next().value);
    cache.set(key, {time:Date.now(), products});
    return products;
  }
  // "New" in the phone filter panel: Shopify's search can't sort by date, so newest first here
  function newest(products) {
    return [...products].sort((a, b) => Number(b.created) - Number(a.created) || String(a.id).localeCompare(String(b.id)));
  }
  return {rank, newest, load, mapPages};
})();
if (!customElements.get('ve-compact-search')) {
  customElements.define('ve-compact-search', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('[name="q"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('.ve-compact-search__suggestions');
      this.status = this.querySelector('[role="status"]');
      this.input.addEventListener('input', () => this.schedule());
      this.input.addEventListener('focus', () => this.schedule());
      this.input.addEventListener('compositionend', () => this.schedule());
      this.form.addEventListener('submit', event => {
        if (!this.input.value.trim()) event.preventDefault();
        this.closeSuggestions();
      });
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) this.closeSuggestions();
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.panel.hidden) {
          event.preventDefault();
          this.dismissEscape = true;
          this.input.focus();
          this.closeSuggestions();
        }
        if (event.key === 'ArrowDown' && !this.panel.hidden) {
          const links = [...this.panel.querySelectorAll('a')];
          const next = links[links.indexOf(document.activeElement) + 1];
          if (next) { event.preventDefault(); next.focus(); }
        }
        if (event.key === 'ArrowUp' && !this.panel.hidden) {
          const links = [...this.panel.querySelectorAll('a')];
          const previous = links[links.indexOf(document.activeElement) - 1] || this.input;
          event.preventDefault(); previous.focus();
        }
      });
      this.addEventListener('keyup', event => {
        if (event.key === 'Escape' && this.dismissEscape) {
          event.stopPropagation();
          this.dismissEscape = false;
        }
      });
    }
    closeSuggestions() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.version = (this.version || 0) + 1;
      this.panel.hidden = true;
      this.panel.replaceChildren();
      this.status.textContent = '';
      this.input.setAttribute('aria-expanded', 'false');
    }
    schedule() {
      this.closeSuggestions();
      const query = this.input.value.trim();
      if (query.length < 2 || this.dataset.enabled !== 'true') return;
      const version = this.version;
      this.timer = setTimeout(() => this.suggest(query, version), 280);
    }
    async suggest(query, version) {
      this.controller = new AbortController();
      const parameters = new URLSearchParams(new FormData(this.form));
      try {
        const products = veSearchRanking.rank(await veSearchRanking.load(this.form.action, parameters, this.controller.signal), query).slice(0, 4);
        if (version !== this.version || query !== this.input.value.trim() || !this.contains(document.activeElement)) return;
        const fragment = document.createDocumentFragment();
        products.forEach(product => {
          const link = document.createElement('a');
          link.className = 've-compact-search__item';
          link.href = product.url;
          if (product.image) {
            const image = document.createElement('img');
            const url = new URL(product.image, location.origin);
            url.searchParams.set('width', '80');
            image.src = url.href; image.alt = ''; image.width = 34; image.height = 44;
            link.append(image);
          }
          const name = document.createElement('span');
          name.textContent = product.title;
          link.append(name); fragment.append(link);
        });
        if (!products.length) {
          const message = document.createElement('p');
          message.textContent = this.dataset.empty;
          fragment.append(message);
          this.status.textContent = this.dataset.empty;
        } else {
          const all = document.createElement('a');
          const url = new URL(this.form.action);
          url.search = new URLSearchParams(new FormData(this.form));
          all.href = url.href; all.className = 've-compact-search__all';
          all.textContent = this.dataset.all; fragment.append(all);
          this.status.textContent = products.map(product => product.title).join('; ');
        }
        this.panel.replaceChildren(fragment);
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error.name !== 'AbortError' && version === this.version) this.closeSuggestions();
      }
    }
    disconnectedCallback() { if (this.ready) this.closeSuggestions(); }
  });
}

async function veRankSearchResults(root) {
  const grid = root.querySelector('.product-grid');
  if (!grid || !root.dataset.veRankingQuery) return;
  // a price sort picked in the phone filter panel: Shopify's own order stands
  const sort = new URLSearchParams(location.search).get('sort_by');
  if (sort && sort !== 'relevance' && sort !== 'created-descending') return;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const parameters = new URLSearchParams(location.search);
    parameters.set('q', root.dataset.veRankingQuery);
    parameters.set('options[prefix]', 'last');
    const loaded = await veSearchRanking.load(root.dataset.veRankingUrl, parameters, controller.signal);
    const products = sort === 'created-descending' ? veSearchRanking.newest(loaded) : veSearchRanking.rank(loaded, root.dataset.veRankingQuery);
    const page = Number(root.dataset.veRankingPage) || 1;
    const wanted = products.slice((page - 1) * 24, page * 24);
    const cards = new Map([...grid.querySelectorAll('[data-ve-search-id]')].map(node => [node.dataset.veSearchId, node]));
    const pages = [...new Set(wanted.filter(product => !cards.has(String(product.id))).map(product => product.page))];
    await veSearchRanking.mapPages(pages, async number => {
      const url = new URL(root.dataset.veRankingUrl, location.origin);
      url.search = parameters;
      url.searchParams.delete('view');
      url.searchParams.set('type', 'product');
      url.searchParams.set('sort_by', 'relevance');
      url.searchParams.set('section_id', root.dataset.veRankingSection);
      url.searchParams.set('page', number);
      const response = await fetch(url, {signal:controller.signal});
      if (!response.ok) throw new Error('Search cards unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      document.querySelectorAll('.product-grid > [data-ve-search-id]').forEach(node => cards.set(node.dataset.veSearchId, node));
    });
    const ordered = wanted.map(product => cards.get(String(product.id)));
    if (!ordered.length || ordered.some(node => !node) || controller.signal.aborted || !root.isConnected) return;
    ordered.forEach(node => {
      node.classList.remove('scroll-trigger', 'animate--slide-in');
      node.removeAttribute('data-cascade');
    });
    grid.replaceChildren(...ordered);
    grid.dataset.veRanked = 'true';
  } catch {
    root.dataset.veRankingFallback = 'true';
  } finally {
    clearTimeout(timeout);
  }
}
document.querySelectorAll('[data-ve-ranking-query]').forEach(veRankSearchResults);
